import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, IconButton, Paper, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { PhotoCamera, DeleteOutline, Edit } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getFullUrl } from '../../utils/urlUtils';

const UploadPlaceholder = styled(Paper)(({ theme }) => ({
    width: '100%',
    minHeight: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px dashed ${theme.palette.grey[400]}`,
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[50],
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.grey[100],
    }
}));

const ImagePreview = styled(Box)(({ theme }) => ({
    width: '100%',
    minHeight: 150,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    overflow: 'hidden',
    '&:hover .overlay': {
        opacity: 1,
    }
}));

const PreviewOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    opacity: 0,
    transition: 'opacity 0.2s',
    cursor: 'default',
}));


const ImageUpload = ({ value, onChange, label, uploading }) => {
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false); // Reset error state when value changes
        
        if (typeof value === 'string' && value.trim() !== '') {
            // Always use getFullUrl to ensure we have the complete URL
            const fullUrl = getFullUrl(value);
            console.log('Setting preview URL:', fullUrl); // Add logging for debugging
            setPreviewUrl(fullUrl);
        } else if (value instanceof File) {
            const localUrl = URL.createObjectURL(value);
            setPreviewUrl(localUrl);
            // Clean up the object URL on component unmount
            return () => URL.revokeObjectURL(localUrl);
        } else {
            setPreviewUrl('');
        }
    }, [value]);
    
    const handleImageError = () => {
        console.error(`🚫 ImageUpload (${label}) failed to load image:`, previewUrl);
        setImageError(true);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            onChange(file);
        }
    };
    
    const handleContainerClick = () => {
        if (!uploading) {
            fileInputRef.current.click();
        }
    };
    
    const handleRemoveImage = (e) => {
        e.stopPropagation();
        setConfirmDelete(true);
    };

    const confirmRemoveImage = () => {
        onChange(null);
        setConfirmDelete(false);
    };

    const cancelRemoveImage = () => {
        setConfirmDelete(false);
    };

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>{label}</Typography>
            {previewUrl ? (
                <Box sx={{ position: 'relative' }}>
                    <ImagePreview sx={{ backgroundImage: `url(${previewUrl})` }}>
                        {/* Hidden img element for error detection */}
                        <img 
                            src={previewUrl} 
                            onError={handleImageError}
                            style={{ display: 'none' }}
                            alt=""
                        />
                        <div className="overlay">
                            <PreviewOverlay>
                                 <Button
                                    variant="contained"
                                    startIcon={<Edit />}
                                    onClick={handleContainerClick}
                                    disabled={uploading}
                                    sx={{ bgcolor: 'rgba(0,0,0,0.7)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                                >
                                    {uploading ? 'Uploading...' : 'Change'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteOutline />}
                                    onClick={handleRemoveImage}
                                    sx={{ bgcolor: 'rgba(220,20,60,0.8)', '&:hover': { bgcolor: 'rgba(220,20,60,0.9)' } }}
                                >
                                    Remove
                                </Button>
                            </PreviewOverlay>
                        </div>
                    </ImagePreview>
                    
                    {/* Small remove button in top-right corner - always visible */}
                    <IconButton 
                        onClick={handleRemoveImage}
                        sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8, 
                            bgcolor: 'error.main', 
                            color: 'white',
                            width: 32,
                            height: 32,
                            '&:hover': { 
                                bgcolor: 'error.dark',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                        }}
                        size="small"
                    >
                        <DeleteOutline fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <UploadPlaceholder elevation={0} variant="outlined" onClick={handleContainerClick}>
                    {uploading ? (
                        <CircularProgress />
                    ) : (
                        <Stack alignItems="center" spacing={1}>
                            <PhotoCamera sx={{ fontSize: 32, color: 'grey.500' }} />
                            <Typography color="text.secondary">Click to upload</Typography>
                        </Stack>
                    )}
                </UploadPlaceholder>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                hidden
            />

            {/* Confirmation Dialog */}
            <Dialog open={confirmDelete} onClose={cancelRemoveImage}>
                <DialogTitle>Remove Image</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this image? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelRemoveImage}>Cancel</Button>
                    <Button onClick={confirmRemoveImage} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImageUpload; 